import { api } from '@packages/backend/convex/_generated/api';
import {
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_TYPE_LABELS,
  FEATURE_FLAG_TYPES,
  FEATURE_FLAGS,
  type FeatureFlagType,
  getRestaurantPlanFeatureFlagKeys,
  RESTAURANT_PLAN_KEYS,
  RESTAURANT_PLANS,
} from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { ColorBadge } from '@/components/ColorBadge';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/a/feature-flags')({
  staticData: {
    breadcrumbs: [{ label: 'Feature flags' }],
  },
  component: FeatureFlagListPage,
});

const typeColor: Record<FeatureFlagType, 'yellow' | 'green' | 'blue' | 'gray'> = {
  product: 'green',
  feature: 'blue',
  internal: 'gray',
  experimental: 'yellow',
};

/**
 * Every feature flag, its type, the plans that include it and the venues it's on for, grouped by
 * type as on a venue's page.
 */
function FeatureFlagListPage() {
  const featureFlagsQuery = useQuery(api.featureFlags.listAll);
  const restaurantsQuery = useQuery(api.restaurants.list);

  if (featureFlagsQuery.isError) {
    return <ErrorAlert message={featureFlagsQuery.error.message} title="Error loading flags" />;
  }
  if (restaurantsQuery.isError) {
    return <ErrorAlert message={restaurantsQuery.error.message} title="Error loading venues" />;
  }
  if (featureFlagsQuery.isPending || restaurantsQuery.isPending) return <LoadingIcon />;

  const restaurantNameById = new Map(
    restaurantsQuery.data.map(({ restaurant }) => [restaurant._id, restaurant.name]),
  );
  const keys = FEATURE_FLAG_TYPES.flatMap((type) =>
    FEATURE_FLAG_KEYS.filter((key) => FEATURE_FLAGS[key].type === type),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature flags</CardTitle>
        <CardDescription>
          Turn a flag on or off from the venue's Feature flags settings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Flag</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Plans</TableHead>
              <TableHead>Venues</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => {
              const { type, label, description } = FEATURE_FLAGS[key];
              const planLabels = RESTAURANT_PLAN_KEYS.filter((planKey) =>
                getRestaurantPlanFeatureFlagKeys(planKey).includes(key),
              ).map((planKey) => RESTAURANT_PLANS[planKey].label);
              const restaurantIds = featureFlagsQuery.data
                .filter((featureFlag) => featureFlag.key === key)
                .map((featureFlag) => featureFlag.restaurantId);
              return (
                <TableRow key={key}>
                  <TableCell className="align-top">
                    <div className="font-medium">{label}</div>
                    <div className="text-muted-foreground text-sm">{description}</div>
                    <code className="text-muted-foreground text-xs">{key}</code>
                  </TableCell>
                  <TableCell className="align-top">
                    <ColorBadge color={typeColor[type]}>
                      {FEATURE_FLAG_TYPE_LABELS[type]}
                    </ColorBadge>
                  </TableCell>
                  <TableCell className="align-top">
                    {planLabels.length === 0 ? (
                      <span className="text-muted-foreground">None</span>
                    ) : (
                      <div className="grid gap-1">
                        {planLabels.map((planLabel) => (
                          <span key={planLabel}>{planLabel}</span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    {restaurantIds.length === 0 ? (
                      <span className="text-muted-foreground">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {restaurantIds.map((restaurantId) => (
                          <Link
                            key={restaurantId}
                            className="underline"
                            params={{ restaurantId }}
                            to="/r/$restaurantId/s/feature-flags"
                          >
                            {restaurantNameById.get(restaurantId) ?? restaurantId}
                          </Link>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
